import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import styles from "../LibraryLiteCutoff/LibraryLiteCutoff.module.css";
import SearchBar from "../../../Components/SearchBar/SearchBar";
import Paging from "../../../Components/Utils/Paging/Paging";
import ConfirmAction from "../../../Components/Utils/ConfirmAction/ConfirmAction";
import { useAppDispatch, useAppSelector } from "../../../Redux/Store/Store";
import { getClient, setLibraryGen3Enabled } from "../../../Redux/Store/Slices/client";
import {
  getLibraryGen3Clients,
  LibraryGen3Client,
  LibraryGen3List,
  updateLibraryGen3,
} from "../../../API/Library/LibraryGen3Rollout";

const PAGE_SIZE = 25;
const SEARCH_DELAY_MS = 300;

type PendingChange =
  | { kind: "all" }
  | { kind: "client"; client: LibraryGen3Client; enabled: boolean };

const LibraryGen3 = () => {
  const dispatch = useAppDispatch();
  const activeClient = useAppSelector(getClient);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<LibraryGen3List | null>(null);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState<PendingChange | null>(null);
  const requestId = useRef(0);

  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(search.trim()), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    const current = ++requestId.current;
    setLoading(true);
    try {
      const result = await getLibraryGen3Clients({
        q: query || undefined,
        page,
        pageSize: PAGE_SIZE,
      });
      if (current === requestId.current) setData(result);
    } catch {
      if (current === requestId.current) toast.error("Failed to load Library rollout");
    } finally {
      if (current === requestId.current) setLoading(false);
    }
  }, [query, page]);

  useEffect(() => {
    void load();
  }, [load]);

  const clients = data?.clients ?? [];
  const pageCount = data?.totalCount ? Math.ceil(data.totalCount / PAGE_SIZE) : 0;

  const applyChange = async () => {
    if (!pending) return;
    try {
      if (pending.kind === "all") {
        await updateLibraryGen3({ scope: "All", enabled: true });
        dispatch(setLibraryGen3Enabled(true));
        toast.success("Gen 3 Library is now on for every client");
      } else {
        await updateLibraryGen3({
          scope: "Single",
          clientId: pending.client.id,
          enabled: pending.enabled,
        });
        if (activeClient?.id === pending.client.id) {
          dispatch(setLibraryGen3Enabled(pending.enabled));
        }
        toast.success(
          pending.enabled
            ? `Gen 3 Library enabled for ${pending.client.name}`
            : `Gen 3 Library turned off for ${pending.client.name}`
        );
      }
    } catch {
      toast.error("Library rollout could not be updated");
      throw new Error("update failed");
    }
    await load();
  };

  const confirmTitle = !pending
    ? ""
    : pending.kind === "all"
      ? "Enable Gen 3 Library for every client?"
      : pending.enabled
        ? `Enable Gen 3 Library for ${pending.client.name}?`
        : `Turn off Gen 3 Library for ${pending.client.name}?`;

  const confirmMessage = !pending
    ? ""
    : pending.kind === "all"
      ? "Every active client will open the new Library from the drawer. Clients that already have it stay on."
      : pending.enabled
        ? `${pending.client.name} will open the new Library instead of the classic one.`
        : `${pending.client.name} will go back to the classic Library in the drawer.`;

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.titleBlock}>
          <p className={styles.eyebrow}>Mesma Portal</p>
          <h1>Library rollout</h1>
          <p className={styles.intro}>
            Choose which clients use the Gen 3 Library. Everyone else keeps the classic Library
            icon, which opens the previous app.
          </p>
        </div>
        <div className={styles.toolbar}>
          <div className={styles.searchWrap}>
            <SearchBar
              value={search}
              onChange={(value) => {
                setSearch(value);
                setPage(1);
              }}
              type=""
            />
          </div>
          <div className={styles.toolbarStats}>
            <span className={styles.statPill}>
              <strong>{data?.enabledCount ?? 0}</strong> client
              {(data?.enabledCount ?? 0) === 1 ? "" : "s"} on Gen 3 Library
            </span>
          </div>
          <button type="button" className={styles.btnPrimary} onClick={() => setPending({ kind: "all" })}>
            Enable for all clients
          </button>
        </div>
      </header>

      <section className={styles.workspace}>
        <div className={styles.panelHeader}>
          <div>
            <h3>Clients</h3>
            <p className={styles.panelSub}>
              {loading ? "Loading…" : `${data?.totalCount ?? 0} active client${data?.totalCount === 1 ? "" : "s"}`}
            </p>
          </div>
        </div>

        {clients.length === 0 ? (
          <div className={styles.emptyState}>
            <p>{loading ? "Loading clients…" : "No clients match your search."}</p>
          </div>
        ) : (
          <ul className={styles.clientList}>
            {clients.map((client) => (
              <li key={client.id} className={styles.clientRow}>
                <div className={styles.clientMain}>
                  <span className={`${styles.statusBadge} ${client.enabled ? styles.statusScheduled : styles.statusNone}`}>
                    {client.enabled ? "ON" : "OFF"}
                  </span>
                  <span className={styles.clientBody}>
                    <span className={styles.clientTitle}>{client.name}</span>
                    <span className={styles.clientMeta}>
                      {client.enabled ? "Opens the Gen 3 Library" : "Opens the classic Library"}
                    </span>
                  </span>
                </div>
                <div className={styles.clientActions}>
                  <button
                    type="button"
                    className={client.enabled ? styles.rowActionDanger : styles.rowAction}
                    onClick={() => setPending({ kind: "client", client, enabled: !client.enabled })}
                  >
                    {client.enabled ? "Turn off" : "Enable"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {pageCount > 1 && (
          <div className={styles.pagingWrap}>
            <Paging activePage={page} pageCount={pageCount} pageChanged={setPage} />
          </div>
        )}
      </section>

      <ConfirmAction
        isVisible={!!pending}
        onClose={() => setPending(null)}
        clickHandler={applyChange}
        title={confirmTitle}
        message={confirmMessage}
        confirmText={pending?.kind === "all" ? "Enable for all" : pending?.enabled ? "Enable" : "Turn off"}
        cancelText="Cancel"
        severity="warning"
      />
    </div>
  );
};

export default LibraryGen3;
